import type {
  AriaProps,
  IAccessibilityPropSDKProps,
} from '../types/vendored/editor-elements-types-a11y';
import type { A11y } from '@wix/editor-react-types';

/**
 * Transforms object keys for accessibility attributes
 * - Keys starting with 'aria' are converted from camelCase to kebab-case (e.g., ariaLabel -> aria-label)
 * - All other keys are converted to lowercase
 * - Values are preserved as-is (can be string, number, or boolean)
 */
export const convertA11yKeysToHtmlFormat = (
  a11yObj: A11y,
): Record<string, string | number | boolean> =>
  Object.entries(a11yObj).reduce(
    (result, [key, value]) => {
      const transformedKey = (
        key.startsWith('aria') ? key.replace(/([A-Z])/, '-$1') : key
      ).toLowerCase();

      return {
        ...result,
        [transformedKey]: value,
      };
    },
    {} as Record<string, string | number | boolean>,
  );

/**
 * Removes 'aria' prefix from accessibility keys and lowercases first letter
 * - Example: ariaLabel -> label, ariaLabelledBy -> labelledBy
 * - Used to match React Components a11y props to Editor Elements Library a11y props
 */
export const removeAriaPrefix = (a11yObj: A11y): AriaProps =>
  Object.entries(a11yObj).reduce((result, [key, value]) => {
    const withoutAria = key.replace(/^aria/, '');
    const normalizedKey =
      withoutAria.charAt(0).toLowerCase() + withoutAria.slice(1);

    return {
      ...result,
      [normalizedKey]: value,
    };
  }, {} as AriaProps);

const keyCodes = {
  enter: 13,
  space: 32,
  end: 35,
  home: 36,
  escape: 27,
  arrowLeft: 37,
  arrowUp: 38,
  arrowRight: 39,
  arrowDown: 40,
  tab: 9,
  delete: 46,
  a: 65,
  z: 90,
  pageUp: 33,
  pageDown: 34,
} as const;

type KeyCode = (typeof keyCodes)[keyof typeof keyCodes];

function activateByKey(key: KeyCode): React.KeyboardEventHandler<HTMLElement> {
  return event => {
    if (event.keyCode === key) {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.click();
    }
  };
}

export const activateBySpaceButton = activateByKey(keyCodes.space);
export const activateByEnterButton = activateByKey(keyCodes.enter);
export const activateBySpaceOrEnterButton: React.KeyboardEventHandler<
  HTMLElement
> = event => {
  activateByEnterButton(event);
  activateBySpaceButton(event);
};

export const HAS_CUSTOM_FOCUS_CLASSNAME = 'has-custom-focus';

const a11yBlackList = ['aria-id', 'aria-metadata', 'aria-type'];

const filteredA11y = (
  a11y: React.AriaAttributes,
  blacklist: Array<string>,
): Record<string, React.AriaAttributes | string> => {
  return Object.entries(a11y).reduce<
    Record<string, React.AriaAttributes | string>
  >((acc, [key, value]) => {
    if (!blacklist.includes(key)) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

export const getTabIndexAttribute = (
  a11y: { tabIndex?: number | string; tabindex?: number | string } = {},
) => {
  const tabIndex = a11y.tabIndex ?? a11y.tabindex ?? undefined;
  return tabIndex !== undefined ? { tabIndex: Number(tabIndex) } : {};
};

export type AriaAttributes = Pick<
  React.AriaAttributes,
  | 'aria-pressed'
  | 'aria-haspopup'
  | 'aria-label'
  | 'aria-live'
  | 'aria-expanded'
  | 'aria-disabled'
  | 'aria-hidden'
  | 'aria-relevant'
  | 'aria-atomic'
  | 'aria-current'
  | 'aria-busy'
  | 'aria-describedby'
  | 'aria-labelledby'
  | 'aria-errormessage'
  | 'aria-owns'
  | 'aria-controls'
  | 'aria-roledescription'
> &
  Pick<React.HTMLAttributes<any>, 'tabIndex' | 'role'> & {
    'aria-label-interactions'?: boolean;
    tabindex?: string;
  };

/**
 * @deprecated use getAccessibilityAttributes instead wherever you can
 */
export const getAriaAttributes = ({
  role,
  tabIndex,
  tabindex,
  ...ariaProps
}: AriaProps = {}): Partial<AriaAttributes> => {
  const result = Object.entries(ariaProps).reduce(
    (prev, [key, value]) => {
      return { ...prev, [`aria-${key}`.toLowerCase()]: value };
    },
    { role, tabIndex: tabIndex ?? tabindex } as Record<
      string,
      string | number | boolean
    >,
  );
  Object.keys(result).forEach(key => {
    if (result[key] === undefined || result[key] === null) {
      // eslint-disable-next-line
      delete result[key];
    }
  });
  return filteredA11y(
    result as Record<string, React.AriaAttributes>,
    a11yBlackList,
  );
};

export const getAccessibilityAttributes = (
  attributes: IAccessibilityPropSDKProps,
) => {
  const {
    role,
    tabIndex,
    tabindex,
    screenReader,
    lang,
    ariaAttributes = {},
  } = attributes;
  const prefixedAriaAttributes: React.AriaAttributes = Object.entries(
    ariaAttributes,
  ).reduce((prev, [key, value]) => {
    return { ...prev, [`aria-${key}`.toLowerCase()]: value };
  }, {});
  return {
    role,
    tabIndex: tabIndex ?? tabindex,
    screenReader,
    ariaAttributes: filteredA11y(
      prefixedAriaAttributes,
      a11yBlackList,
    ) as React.AriaAttributes,
    lang,
  };
};

export const getAccessibilityAttributesSpread = (
  attributes: IAccessibilityPropSDKProps,
) => {
  const a11yProps = getAccessibilityAttributes(attributes);
  return {
    ...a11yProps.ariaAttributes,
    tabIndex: a11yProps.tabIndex,
    screenReader: a11yProps.screenReader,
    lang: a11yProps.lang,
    role: a11yProps.role,
  };
};
